/**
 * Centralized word lists for student login code generation.
 *
 * Each login is an adjective + noun pair (e.g. "veselý orel", "rýchla mačka").
 * Gender agreement is enforced automatically: feminine nouns get feminine
 * adjectives, masculine nouns get masculine adjectives.
 *
 * Adjectives are stored in masculine form only; feminine forms are derived
 * programmatically (Czech: -ý→-á, -í stays; Slovak: -ý→-á, -y→-a, -í→-ia).
 *
 * Country codes ("cz"/"sk") map to the respective language word lists.
 *
 * Word-list sizes differ by language release; combinations scale as
 * |adjectives| × (|feminine nouns| + |masculine nouns|).
 */

export type StudentCountry = "cz" | "sk";

// ---------------------------------------------------------------------------
// Feminine-form derivation
// ---------------------------------------------------------------------------

function deriveCzechFeminine(masculine: string): string {
  if (masculine.endsWith("ý")) return masculine.slice(0, -1) + "á";
  if (masculine.endsWith("í")) return masculine; // soft adjectives: same
  return masculine;
}

function deriveSlovakFeminine(masculine: string): string {
  if (masculine.endsWith("ý")) return masculine.slice(0, -1) + "á";
  if (masculine.endsWith("y")) return masculine.slice(0, -1) + "a";
  if (masculine.endsWith("í")) return masculine.slice(0, -1) + "ia";
  if (masculine.endsWith("i")) return masculine.slice(0, -1) + "a";
  return masculine;
}

// ---------------------------------------------------------------------------
// Czech word lists
// ---------------------------------------------------------------------------

const CS_ADJ = [
  // Colors
  "žlutý",
  "fialový",
  "růžový",
  "bílý",
  "černý",
  "zlatý",
  "stříbrný",
  "barevný",
  "duhový",
  "pruhovaný",
  "strakatý",
  // Size / dimension
  "velký",
  "malý",
  "široký",
  "obrovský",
  "drobný",
  "mohutný",
  "nepatrný",
  "štíhlý",
  // Surface / texture
  "drsný",
  // Speed / movement
  "rychlý",
  "pomalý",
  "hbitý",
  "svižný",
  "mrštný",
  "čilý",
  "obratný",
  "ladný",
  // Sound
  "tichý",
  // Character – positive
  "veselý",
  "klidný",
  "silný",
  "chytrý",
  "statečný",
  "hravý",
  "šikovný",
  "odvážný",
  "laskavý",
  "zvědavý",
  "bystrý",
  "pilný",
  "trpělivý",
  "vtipný",
  "moudrý",
  "hodný",
  "milý",
  "přátelský",
  "skromný",
  "nadšený",
  "pečlivý",
  "pozorný",
  "upřímný",
  "srdečný",
  "zdvořilý",
  "čestný",
  "ušlechtilý",
  "pracovitý",
  "vytrvalý",
  "houževnatý",
  "neúnavný",
  // Character – fun / epic
  "tajemný",
  "slavný",
  "zábavný",
  "mazlivý",
  "divoký",
  "smělý",
  "magický",
  "královský",
  "kosmický",
  "fantastický",
  "pohádkový",
  "bájný",
  "mocný",
  "legendární",
  // Additional descriptive
  "ohnivý",
  "udatný",
  "zdatný",
  "pohotový",
  "důvtipný",
  "rozvážný",
  "roztomilý",
  "půvabný",
  "nádherný",
  "skvělý",
  "úžasný",
  "báječný",
  "zázračný",
  "kouzelný",
  // Refined / style
  "vznešený",
  "noblesní",
  "elegantní",
  "galantní",
  "energický",
  "exotický",
  // More traits
  "svěží",
  "mladý",
  "prastarý",
  "bohatý",
];

const CS_NOUNS_F = [
  // Mammals
  "kočka",
  "myš",
  "liška",
  "veverka",
  "panda",
  "žirafa",
  "zebra",
  "opice",
  "vydra",
  "ovce",
  "lama",
  "gazela",
  "antilopa",
  "gorila",
  "lvice",
  "lasice",
  "kuna",
  "ondatra",
  "nutrie",
  "laň",
  "srna",
  // Birds
  "sova",
  "labuť",
  "vlaštovka",
  "sýkora",
  "andulka",
  "kachna",
  "vrána",
  "straka",
  "kukačka",
  "pěnkava",
  "volavka",
  "kavka",
  "hrdlička",
  "křepelka",
  "jiřička",
  "poštolka",
  // Reptiles / amphibians
  "želva",
  "ještěrka",
  "žába",
  "krajta",
  "kobra",
  "rosnička",
  // Fish
  "ryba",
  "štika",
  // Insects / small creatures
  "včela",
  "vážka",
  "beruška",
  "kobylka",
  "moucha",
  "můra",
  "housenka",
  "stonožka",
  "světluška",
];

const CS_NOUNS_M = [
  // Mammals
  "pes",
  "vlk",
  "medvěd",
  "jelen",
  "lev",
  "tygr",
  "leopard",
  "gepard",
  "los",
  "srnec",
  "rys",
  "jezevec",
  "bizon",
  "sob",
  "nosorožec",
  "hroch",
  "slon",
  "klokan",
  "ježek",
  "krtek",
  "zajíc",
  "dikobraz",
  "králík",
  "křeček",
  "beran",
  "sysel",
  "svišť",
  // Birds
  "orel",
  "sokol",
  "datel",
  "tučňák",
  "papoušek",
  "vrabec",
  "havran",
  "čáp",
  "kos",
  "slavík",
  "ledňáček",
  "pelikán",
  "plameňák",
  "kolibřík",
  "kondor",
  "dudek",
  "výr",
  "luňák",
  "jestřáb",
  "sýček",
  "sup",
  // Reptiles / marine
  "had",
  "krokodýl",
  "chameleon",
  "leguán",
  "delfín",
  "žralok",
  "rejnok",
  "mečoun",
  "úhoř",
  "mlok",
  "gekon",
  "rak",
  "krab",
  // Fish
  "kapr",
  "sumec",
  // Insects / small creatures
  "brouk",
  "mravenec",
  "cvrček",
  "pavouk",
  "motýl",
  "šnek",
];

// ---------------------------------------------------------------------------
// Slovak word lists
// ---------------------------------------------------------------------------

const SK_ADJ = [
  // Colors (SK equivalents of CS_ADJ)
  "žltý",
  "fialový",
  "ružový",
  "biely",
  "čierny",
  "zlatý",
  "strieborný",
  "farebný",
  "dúhový",
  "pruhovaný",
  "strakatý",
  // Size / dimension
  "veľký",
  "malý",
  "široký",
  "obrovský",
  "drobný",
  "mohutný",
  "nepatrný",
  "štíhly",
  // Surface / texture
  "drsný",
  // Speed / movement
  "rýchly",
  "pomalý",
  "hbitý",
  "svižný",
  "mrštný",
  "čulý",
  "obratný",
  "ladný",
  // Sound
  "tichý",
  // Character – positive
  "veselý",
  "pokojný",
  "silný",
  "dômyselný",
  "statočný",
  "hravý",
  "šikovný",
  "odvážny",
  "láskavý",
  "zvedavý",
  "bystrý",
  "pilný",
  "trpezlivý",
  "vtipný",
  "múdry",
  "dobrý",
  "milý",
  "priateľský",
  "skromný",
  "nadšený",
  "svedomitý",
  "pozorný",
  "úprimný",
  "srdečný",
  "zdvorilý",
  "čestný",
  "ušľachtilý",
  "pracovitý",
  "vytrvalý",
  "húževnatý",
  "neúnavný",
  // Character – fun / epic
  "tajomný",
  "slávny",
  "zábavný",
  "maznivý",
  "divoký",
  "smelý",
  "magický",
  "kráľovský",
  "kozmický",
  "fantastický",
  "rozprávkový",
  "bájny",
  "mocný",
  "legendárny",
  // Additional descriptive
  "ohnivý",
  "udatný",
  "zdatný",
  "pohotový",
  "dôvtipný",
  "rozvážny",
  "roztomilý",
  "pôvabný",
  "nádherný",
  "skvelý",
  "úžasný",
  "báječný",
  "zázračný",
  "kúzelný",
  // Refined / style
  "vznešený",
  "noblesný",
  "elegantný",
  "galantný",
  "energický",
  "exotický",
  // More traits
  "svieži",
  "mladý",
  "prastarý",
  "bohatý",
];

const SK_NOUNS_F = [
  // Mammals
  "mačka",
  "myš",
  "líška",
  "veverička",
  "panda",
  "žirafa",
  "zebra",
  "opica",
  "vydra",
  "ovca",
  "lama",
  "gazela",
  "antilopa",
  "gorila",
  "levica",
  "lasica",
  "kuna",
  "ondatra",
  "nutria",
  "laň",
  "srnka",
  // Birds
  "sova",
  "labuť",
  "lastovička",
  "sýkorka",
  "andulka",
  "kačka",
  "vrana",
  "straka",
  "kukučka",
  "pinka",
  "volavka",
  "kavka",
  "hrdlička",
  "prepelička",
  "dážďovka",
  "poštolka",
  // Reptiles / amphibians
  "korytnačka",
  "jašterica",
  "žaba",
  "krajta",
  "kobra",
  "rosnička",
  // Fish
  "ryba",
  "šťuka",
  // Insects / small creatures
  "včela",
  "vážka",
  "lienka",
  "kobylka",
  "mucha",
  "moľa",
  "húsenica",
  "stonožka",
  "svetluška",
];

const SK_NOUNS_M = [
  // Mammals
  "pes",
  "vlk",
  "medveď",
  "jeleň",
  "lev",
  "tiger",
  "leopard",
  "gepard",
  "los",
  "srnec",
  "rys",
  "jazvec",
  "bizón",
  "sob",
  "nosorožec",
  "hroch",
  "slon",
  "klokan",
  "jež",
  "krt",
  "zajac",
  "dikobraz",
  "králik",
  "škrečok",
  "baran",
  "sysel",
  "svišť",
  // Birds
  "orol",
  "sokol",
  "ďateľ",
  "tučniak",
  "papagáj",
  "vrabec",
  "havran",
  "bocian",
  "drozd",
  "slávik",
  "rybárik",
  "pelikán",
  "plameniak",
  "kolibrík",
  "kondor",
  "dudok",
  "výr",
  "luniak",
  "jastrab",
  "výrik",
  "sup",
  // Reptiles / marine
  "had",
  "krokodíl",
  "chameleón",
  "leguán",
  "delfín",
  "žralok",
  "raja",
  "mečiar",
  "úhor",
  "mlok",
  "gekón",
  "rak",
  "krab",
  // Fish
  "kapor",
  "sumec",
  // Insects / small creatures
  "chrobák",
  "mravec",
  "svrček",
  "pavúk",
  "motýľ",
  "slimák",
];

// ---------------------------------------------------------------------------
// Generation logic
// ---------------------------------------------------------------------------

interface WordBank {
  adjMasc: string[];
  nounsF: string[];
  nounsM: string[];
  deriveFem: (m: string) => string;
}

const BANKS: Record<StudentCountry, WordBank> = {
  cz: { adjMasc: CS_ADJ, nounsF: CS_NOUNS_F, nounsM: CS_NOUNS_M, deriveFem: deriveCzechFeminine },
  sk: { adjMasc: SK_ADJ, nounsF: SK_NOUNS_F, nounsM: SK_NOUNS_M, deriveFem: deriveSlovakFeminine },
};

/**
 * Generate a random gender-correct adjective + noun pair for a student login.
 */
export function generateStudentLoginWords(country: StudentCountry = "cz"): {
  word1: string;
  word2: string;
} {
  const bank = BANKS[country];
  const totalNouns = bank.nounsF.length + bank.nounsM.length;
  const nounIdx = Math.floor(Math.random() * totalNouns);
  const isFem = nounIdx < bank.nounsF.length;
  const noun = isFem ? bank.nounsF[nounIdx] : bank.nounsM[nounIdx - bank.nounsF.length];

  const adjMasc = bank.adjMasc[Math.floor(Math.random() * bank.adjMasc.length)];
  const adjective = isFem ? bank.deriveFem(adjMasc) : adjMasc;

  return { word1: adjective, word2: noun };
}

/**
 * Total number of unique login combinations for a given country.
 */
export function getLoginCombinationCount(country: StudentCountry = "cz"): number {
  const bank = BANKS[country];
  return bank.adjMasc.length * (bank.nounsF.length + bank.nounsM.length);
}
